import { useState, useEffect, useMemo } from 'react';
import { Outlet, useParams, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import BorrowerHeader from './BorrowerHeader';
import BorrowerNav from './BorrowerNav';
import BorrowerLoanContext from './BorrowerLoanContext';
import { otpBorrowerLoansMe } from '../../service/otpApi';

const APP_BAR_MOBILE = 64;
const APP_BAR_DESKTOP = 80;

const StyledRoot = styled('div')({
  display: 'flex',
  minHeight: '100%',
  overflow: 'hidden',
});

const Main = styled('div')(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  minHeight: '100%',
  paddingTop: APP_BAR_MOBILE + 24,
  paddingBottom: theme.spacing(10),
  [theme.breakpoints.up('lg')]: {
    paddingTop: APP_BAR_DESKTOP + 24,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

export default function BorrowerLayout() {
  const [open, setOpen] = useState(false);
  const { loanId } = useParams();
  const location = useLocation();
  const [loan, setLoan] = useState(location.state?.loan || {});
  const [loading, setLoading] = useState(!location.state?.loan);

  // If we don't have loan data from navigation state (e.g. direct URL), fetch it
  useEffect(() => {
    if (loan?.loanNumber) return; // already have it

    const fetchLoan = async () => {
      try {
        setLoading(true);
        const res = await otpBorrowerLoansMe();
        const loans = res?.data?.loans || [];
        const match = loans.find((l) => l.loanId === loanId);
        if (match) setLoan(match);
      } catch (err) {
        console.error('Failed to fetch loan data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchLoan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loanId]);

  const contextValue = useMemo(
    () => ({ loanId, loan, loading }),
    [loanId, loan, loading]
  );

  return (
    <BorrowerLoanContext.Provider value={contextValue}>
      <StyledRoot>
        <BorrowerHeader onOpenNav={() => setOpen(true)} />
        <BorrowerNav openNav={open} onCloseNav={() => setOpen(false)} />
        <Main>
          <Outlet />
        </Main>
      </StyledRoot>
    </BorrowerLoanContext.Provider>
  );
}
